'use strict'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import {
  makeStyles,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Checkbox,
  ListItemText,
  ListSubheader,
  Tooltip
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { GoogleMap, LoadScript, Marker, InfoWindow, Polyline } from '@react-google-maps/api'
import { isSameDay, format, startOfWeek, addDays, getDay } from 'date-fns'
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar'
import ScheduleIcon from '@material-ui/icons/Schedule'
import PlaceIcon from '@material-ui/icons/Place'
import TimelineIcon from '@material-ui/icons/Timeline'
import RefreshIcon from '@material-ui/icons/Refresh'
import WarningIcon from '@material-ui/icons/Warning'
import EventNoteIcon from '@material-ui/icons/EventNote'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  datePicker: {
    width: 200
  },
  crewSelect: {
    minWidth: 220,
    maxWidth: 300
  },
  statsChip: {
    marginLeft: 'auto'
  },
  mapContainer: {
    width: '100%',
    height: '60vh',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  },
  dayChips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem'
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: '50%'
  },
  infoWindow: {
    padding: theme.spacing(1),
    minWidth: 150
  },
  infoTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5)
  },
  infoCrew: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary
  },
  infoOrder: {
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  // Route Stats Panel
  routeStatsPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: '#fff'
  },
  statBox: {
    textAlign: 'center',
    padding: theme.spacing(1)
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5)
  },
  optimizeButton: {
    marginLeft: theme.spacing(2)
  },
  optimizedBadge: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1)
  },
  preCalculatedBadge: {
    backgroundColor: theme.palette.info.main,
    color: '#fff',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1)
  },
  notOptimizedBadge: {
    backgroundColor: theme.palette.warning.main,
    color: '#fff',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  otWarning: {
    color: theme.palette.warning.main,
    fontWeight: 600
  }
}))

// Map center (Marin County area)
const defaultCenter = {
  lat: 37.9735,
  lng: -122.5311
}

const mapContainerStyle = {
  width: '100%',
  height: '100%'
}

// Distinct colors for crew leaders
const crewColors = [
  '#FF5733', '#33FF57', '#3357FF', '#FF33E3', '#33FFE3',
  '#FFFF33', '#FF8C33', '#8C33FF', '#33FF8C', '#FF338C',
  '#33FFFF', '#FFFF33', '#8CFF33', '#338CFF', '#FF3333',
  '#33FF33', '#3333FF', '#FF33FF', '#33FFFF', '#FFFF00'
]

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

// Default crew size of 3 if not available
const DEFAULT_CREW_SIZE = 3

// Format minutes to hours:minutes
const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

// Map day names to day numbers (0 = Sunday, 1 = Monday, etc.)
const dayNameToNumber = {
  'Monday': 1,
  'Tuesday': 2,
  'Wednesday': 3,
  'Thursday': 4,
  'Friday': 5
}

function RoutingPropertiesMapView({ properties, url }) {
  const classes = useStyles()
  const history = useHistory()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedCrewLeaders, setSelectedCrewLeaders] = useState([]) // Empty = all selected
  const [activeMarker, setActiveMarker] = useState(null)
  const [mapRef, setMapRef] = useState(null)

  // Daily routes from server (pre-calculated)
  const [dailyRoutes, setDailyRoutes] = useState([])
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false)
  const [routesError, setRoutesError] = useState(null)

  // Route optimization state (for manual re-optimization)
  const [optimizedRoute, setOptimizedRoute] = useState(null)
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [optimizeError, setOptimizeError] = useState(null)

  // Get the current day of week from selectedDate (for highlighting the chip)
  const currentDayOfWeek = days[getDay(selectedDate) - 1] // getDay returns 0=Sun, so Mon=1 maps to days[0]

  // Fetch daily routes when date changes
  useEffect(() => {
    const fetchDailyRoutes = async () => {
      setIsLoadingRoutes(true)
      setRoutesError(null)
      setOptimizedRoute(null) // Clear any manual optimization

      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd')
        const response = await axios.get(`${url}/api/aspire/routing/daily-routes`, {
          params: { date: dateStr }
        })

        if (response.data?.success) {
          setDailyRoutes(response.data.routes || [])
          console.log(`[DailyRoutes] Loaded ${response.data.routes?.length || 0} routes for ${dateStr}`)
        } else {
          setDailyRoutes([])
        }
      } catch (error) {
        console.error('Error fetching daily routes:', error)
        setRoutesError(error.message)
        setDailyRoutes([])
      } finally {
        setIsLoadingRoutes(false)
      }
    }

    fetchDailyRoutes()
  }, [selectedDate, url])

  // Extract unique crew leaders from daily routes (preferred) or properties
  const allCrewLeaders = useMemo(() => {
    const crewMap = new Map()

    // First, add from daily routes (these are the ones with pre-calculated data)
    dailyRoutes.forEach(route => {
      if (route.crewLeaderId && route.crewLeaderName) {
        crewMap.set(route.crewLeaderId, route.crewLeaderName)
      }
    })

    // Also add from properties for completeness
    properties.forEach(prop => {
      prop.assignedCrewLeaders?.forEach(crew => {
        if (crew.id && crew.name) {
          crewMap.set(crew.id, crew.name)
        }
      })
      prop.futureVisits?.forEach(visit => {
        if (visit.crewLeaderId && visit.crewLeaderName) {
          crewMap.set(visit.crewLeaderId, visit.crewLeaderName)
        }
      })
    })
    return Array.from(crewMap, ([id, name]) => ({ id, name }))
  }, [dailyRoutes, properties])

  // Create color map for crew leaders
  const crewColorMap = useMemo(() => {
    const map = new Map()
    allCrewLeaders.forEach((crew, index) => {
      map.set(crew.id, crewColors[index % crewColors.length])
    })
    return map
  }, [allCrewLeaders])

  // Filter properties based on selected date and crew leaders
  const filteredProperties = useMemo(() => {
    return properties.filter(prop => {
      if (!prop.latitude || !prop.longitude) return false

      const matchingVisits = prop.futureVisits?.filter(visit => {
        const visitDate = new Date(visit.date)

        // Always filter by the specific selected date
        if (!isSameDay(visitDate, selectedDate)) return false

        // Crew leader filter - empty array means all, otherwise check if in selection
        if (selectedCrewLeaders.length > 0) {
          if (!selectedCrewLeaders.includes(visit.crewLeaderId)) return false
        }

        return true
      })

      return matchingVisits && matchingVisits.length > 0
    })
  }, [properties, selectedDate, selectedCrewLeaders])

  // Reset optimized route when crew selection changes
  useEffect(() => {
    setOptimizedRoute(null)
    setOptimizeError(null)
  }, [selectedCrewLeaders])

  // Get the selected daily route (if crew leader is selected and we have pre-calculated data)
  const selectedDailyRoute = useMemo(() => {
    if (selectedCrewLeaders.length === 1) {
      // Single crew selected - find their pre-calculated route
      return dailyRoutes.find(r => r.crewLeaderId === selectedCrewLeaders[0]) || null
    }
    return null
  }, [dailyRoutes, selectedCrewLeaders])

  // Combined route stats from all selected crews (or all if none selected)
  const combinedRouteStats = useMemo(() => {
    const relevantRoutes = selectedCrewLeaders.length > 0
      ? dailyRoutes.filter(r => selectedCrewLeaders.includes(r.crewLeaderId))
      : dailyRoutes

    if (relevantRoutes.length === 0) return null

    return {
      totalStops: relevantRoutes.reduce((sum, r) => sum + (r.totalStops || 0), 0),
      totalServiceTime: relevantRoutes.reduce((sum, r) => sum + (r.totalServiceTime || 0), 0),
      totalDriveTime: relevantRoutes.reduce((sum, r) => sum + (r.totalDriveTime || 0), 0),
      totalMiles: relevantRoutes.reduce((sum, r) => sum + (r.totalMiles || 0), 0),
      totalHours: relevantRoutes.reduce((sum, r) => sum + (r.totalHours || 0), 0),
      estimatedOTHours: relevantRoutes.reduce((sum, r) => sum + (r.estimatedOTHours || 0), 0),
      allOptimized: relevantRoutes.every(r => r.isOptimized),
      routeCount: relevantRoutes.length
    }
  }, [dailyRoutes, selectedCrewLeaders])

  // Get the properties to display (from daily route or filtered properties)
  const displayProperties = useMemo(() => {
    // If manual optimization was done, use that
    if (optimizedRoute?.stops?.length > 0) {
      return optimizedRoute.stops.map(stop => {
        const prop = filteredProperties.find(p =>
          (p.propertyId || p._id)?.toString() === stop.siteId?.toString()
        )
        return {
          ...prop,
          ...stop,
          _optimizedOrder: stop.order
        }
      }).filter(Boolean)
    }

    // If single crew selected and has pre-calculated route, use that order
    if (selectedDailyRoute?.stops?.length > 0) {
      return selectedDailyRoute.stops.map((stop, index) => {
        // Find the full property data
        const prop = properties.find(p => p.propertyId === stop.propertyId)
        return {
          ...prop,
          ...stop,
          latitude: stop.latitude || prop?.latitude,
          longitude: stop.longitude || prop?.longitude,
          _optimizedOrder: stop.order || index + 1,
          _fromDailyRoute: true
        }
      }).filter(p => p.latitude && p.longitude)
    }

    // Otherwise, show filtered properties
    return filteredProperties
  }, [optimizedRoute, selectedDailyRoute, filteredProperties, properties])

  // Route polyline path (show if we have optimized or pre-calculated route)
  const routePath = useMemo(() => {
    const hasRoute = optimizedRoute?.stops?.length > 0 || selectedDailyRoute?.isOptimized
    if (!hasRoute) return []
    return displayProperties.map(prop => ({
      lat: prop.latitude,
      lng: prop.longitude
    }))
  }, [optimizedRoute, selectedDailyRoute, displayProperties])

  // Determine if we're showing pre-calculated data
  const isShowingPreCalculated = !optimizedRoute && selectedDailyRoute?.isOptimized
  const isShowingUnoptimized = !optimizedRoute && selectedDailyRoute && !selectedDailyRoute.isOptimized

  // Get crew leader for a property
  const getCrewLeaderForProperty = (prop) => {
    const visit = prop.futureVisits?.find(v => {
      const visitDate = new Date(v.date)
      return isSameDay(visitDate, selectedDate)
    })
    return visit ? { id: visit.crewLeaderId, name: visit.crewLeaderName } : null
  }

  // Handle day chip click - changes date to that day of the current week
  const handleDayClick = (day) => {
    const targetDayNumber = dayNameToNumber[day]
    // Get the start of the week (Sunday) for the currently selected date
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 }) // 0 = Sunday
    // Add days to get to the target day
    const newDate = addDays(weekStart, targetDayNumber)
    setSelectedDate(newDate)
  }

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date)
  }

  // Optimize route
  const handleOptimizeRoute = useCallback(async () => {
    if (filteredProperties.length < 2) {
      setOptimizeError('Need at least 2 properties to optimize a route')
      return
    }

    setIsOptimizing(true)
    setOptimizeError(null)

    try {
      const response = await axios.post(`${url}/api/aspire/routing/optimize`, {
        properties: filteredProperties
      })

      if (response.data?.success && response.data?.route) {
        setOptimizedRoute(response.data.route)
      } else {
        setOptimizeError(response.data?.message || 'Optimization failed')
      }
    } catch (error) {
      console.error('Optimization error:', error)
      setOptimizeError(error.response?.data?.message || error.message)
    } finally {
      setIsOptimizing(false)
    }
  }, [url, filteredProperties])

  // Fit map bounds to markers
  useEffect(() => {
    if (mapRef && displayProperties.length > 0) {
      const bounds = new window.google.maps.LatLngBounds()
      displayProperties.forEach(prop => {
        bounds.extend({ lat: prop.latitude, lng: prop.longitude })
      })
      mapRef.fitBounds(bounds)
    }
  }, [mapRef, displayProperties])

  const onMapLoad = (map) => {
    setMapRef(map)
  }

  // Calculate total service time from filtered properties using future visit estimatedHours
  const totalServiceTime = useMemo(() => {
    console.log('=== Calculating Service Time ===')
    console.log(`Filtered properties count: ${filteredProperties.length}`)

    const total = filteredProperties.reduce((sum, prop, index) => {
      // Find the matching future visit to get the correct estimatedHours (already divided by crew size)
      const matchingVisit = prop.futureVisits?.find(visit => {
        const visitDate = new Date(visit.date)
        return isSameDay(visitDate, selectedDate)
      })
      console.log('[Matching Visit] - ', matchingVisit)
      // Use estimatedHours from the visit (already adjusted for crew size), fallback to property-level
      const visitEstimatedHours = matchingVisit?.estimatedHours
      const visitManHours = matchingVisit?.estimatedManHours
      const visitCrewSize = matchingVisit?.crewSize || DEFAULT_CREW_SIZE
      const propEstimatedHours = prop.estimatedHours
      const propAvgActualHours = prop.avgActualHours



      // Prefer visit-level hours (already divided by crew size), then property-level divided by default crew size
      let hoursUsed
      if (propAvgActualHours) {
        hoursUsed = propAvgActualHours / DEFAULT_CREW_SIZE
      } else if (propEstimatedHours) {
        // Property-level hours are man-hours, divide by default crew size
        hoursUsed = propEstimatedHours / DEFAULT_CREW_SIZE
      } else if (visitEstimatedHours) {
        hoursUsed = visitEstimatedHours / DEFAULT_CREW_SIZE
      } else {
        hoursUsed = 1
      }

      const minutesForProp = hoursUsed * 60

      console.log(`Property ${index + 1}: "${prop.name}"`)
      console.log(`  - Visit: manHours=${visitManHours}, crewSize=${visitCrewSize}, estimatedHours=${visitEstimatedHours}`)
      console.log(`  - Property: estimatedHours=${propEstimatedHours}, avgActualHours=${propAvgActualHours}`)
      console.log(`  - Using: ${hoursUsed} hours (${minutesForProp} minutes)`)

      return sum + minutesForProp
    }, 0)

    console.log(`Total service time: ${total} minutes (${(total / 60).toFixed(2)} hours)`)
    console.log('================================')

    return total
  }, [filteredProperties, selectedDate])

  return (
    <div className={classes.root}>
      {/* Filter Bar */}
      <Paper className={classes.filterBar}>
        <KeyboardDatePicker
          className={classes.datePicker}
          label="Select Date"
          format="MM/dd/yyyy"
          value={selectedDate}
          onChange={handleDateChange}
          inputVariant="outlined"
          size="small"
          autoOk
          disableToolbar={false}
        />

        <FormControl variant="outlined" size="small" className={classes.crewSelect}>
          <InputLabel>Crew Leaders</InputLabel>
          <Select
            multiple
            value={selectedCrewLeaders}
            onChange={(e) => setSelectedCrewLeaders(e.target.value)}
            label="Crew Leaders"
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Crew Leaders'
              if (selected.length === allCrewLeaders.length) return 'All Crew Leaders'
              if (selected.length === 1) {
                const crew = allCrewLeaders.find(c => c.id === selected[0])
                return crew?.name || '1 selected'
              }
              return `${selected.length} selected`
            }}
            displayEmpty
          >
            <ListSubheader disableSticky style={{ backgroundColor: '#f5f5f5', lineHeight: '36px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" px={1}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedCrewLeaders(allCrewLeaders.map(c => c.id))
                  }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedCrewLeaders([])
                  }}
                >
                  Clear
                </Button>
              </Box>
            </ListSubheader>
            {allCrewLeaders.map(crew => (
              <MenuItem key={crew.id} value={crew.id}>
                <Checkbox checked={selectedCrewLeaders.includes(crew.id)} color="primary" />
                <ListItemText primary={crew.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box className={classes.dayChips}>
          <Typography variant="body2" style={{ marginRight: 8, alignSelf: 'center' }}>
            Day:
          </Typography>
          {days.map(day => (
            <Chip
              key={day}
              label={day.substring(0, 3)}
              size="small"
              color={currentDayOfWeek === day ? 'primary' : 'default'}
              onClick={() => handleDayClick(day)}
              variant={currentDayOfWeek === day ? 'default' : 'outlined'}
            />
          ))}
        </Box>

        {isLoadingRoutes ? (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading routes...</Typography>
          </Box>
        ) : (
          <>
            <Tooltip title={isShowingPreCalculated ? 'Re-optimize this route' : 'Calculate optimal route order'}>
              <Button
                variant={isShowingPreCalculated ? 'outlined' : 'contained'}
                color="primary"
                size="small"
                className={classes.optimizeButton}
                onClick={handleOptimizeRoute}
                disabled={isOptimizing || displayProperties.length < 2}
                startIcon={isOptimizing ? <CircularProgress size={16} color="inherit" /> : (isShowingPreCalculated ? <RefreshIcon /> : <TimelineIcon />)}
              >
                {isOptimizing ? 'Optimizing...' : (isShowingPreCalculated ? 'Re-optimize' : 'Optimize Route')}
              </Button>
            </Tooltip>

            {optimizedRoute && (
              <span className={classes.optimizedBadge}>Optimized</span>
            )}

            {isShowingPreCalculated && !optimizedRoute && (
              <span className={classes.preCalculatedBadge}>Pre-calculated</span>
            )}

            {isShowingUnoptimized && (
              <Tooltip title={selectedDailyRoute?.optimizationError || 'Route could not be optimized'}>
                <span className={classes.notOptimizedBadge}>
                  <WarningIcon style={{ fontSize: 14 }} /> Not Optimized
                </span>
              </Tooltip>
            )}

            {dailyRoutes.length > 0 && selectedCrewLeaders.length === 0 && (
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                {dailyRoutes.length} crew{dailyRoutes.length > 1 ? 's' : ''} scheduled
              </Typography>
            )}

            {/* View Schedule Button */}
            <Button
              variant="outlined"
              color="default"
              size="small"
              onClick={() => {
                const dateStr = format(selectedDate, 'yyyy-MM-dd')
                const crewId = selectedCrewLeaders.length === 1 ? selectedCrewLeaders[0] : ''
                window.open(`/routing/schedule/${crewId}/${dateStr}`, '_blank')
              }}
              startIcon={<EventNoteIcon />}
              style={{ marginLeft: 8 }}
            >
              View Schedule
            </Button>
          </>
        )}
      </Paper>

      {/* Route Stats Panel */}
      <Paper className={classes.routeStatsPanel}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <Box className={classes.statBox}>
              <Typography className={classes.statValue}>
                {combinedRouteStats?.totalStops || displayProperties.length}
              </Typography>
              <Typography className={classes.statLabel}>
                <PlaceIcon fontSize="small" /> Properties
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Box className={classes.statBox}>
              <Typography className={classes.statValue}>
                {formatDuration(
                  optimizedRoute?.totalServiceTime ||
                  combinedRouteStats?.totalServiceTime ||
                  totalServiceTime
                )}
              </Typography>
              <Typography className={classes.statLabel}>
                <ScheduleIcon fontSize="small" /> Service Time
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Box className={classes.statBox}>
              <Typography
                className={classes.statValue}
                style={{ color: (optimizedRoute || combinedRouteStats?.allOptimized) ? '#4caf50' : '#999' }}
              >
                {optimizedRoute
                  ? formatDuration(optimizedRoute.totalDriveTime)
                  : combinedRouteStats?.totalDriveTime
                    ? formatDuration(combinedRouteStats.totalDriveTime)
                    : '--'
                }
              </Typography>
              <Typography className={classes.statLabel}>
                <DirectionsCarIcon fontSize="small" /> Drive Time
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Box className={classes.statBox}>
              <Typography
                className={classes.statValue}
                style={{ color: (optimizedRoute || combinedRouteStats?.allOptimized) ? '#4caf50' : '#999' }}
              >
                {optimizedRoute
                  ? `${optimizedRoute.totalMilesDriven} mi`
                  : combinedRouteStats?.totalMiles
                    ? `${combinedRouteStats.totalMiles.toFixed(1)} mi`
                    : '--'
                }
              </Typography>
              <Typography className={classes.statLabel}>
                <DirectionsCarIcon fontSize="small" /> Total Miles
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Box className={classes.statBox}>
              <Typography
                className={classes.statValue}
                style={{ color: (optimizedRoute || combinedRouteStats) ? '#2196f3' : '#999' }}
              >
                {optimizedRoute
                  ? formatDuration(optimizedRoute.totalTime)
                  : combinedRouteStats?.totalHours
                    ? `${combinedRouteStats.totalHours.toFixed(1)}h`
                    : formatDuration(totalServiceTime) + ' + drive'
                }
              </Typography>
              <Typography className={classes.statLabel}>
                <ScheduleIcon fontSize="small" /> Total Time
              </Typography>
            </Box>
          </Grid>

          {/* OT Hours */}
          {combinedRouteStats?.estimatedOTHours > 0 && (
            <Grid item xs={6} sm={3} md={2}>
              <Box className={classes.statBox}>
                <Typography className={`${classes.statValue} ${classes.otWarning}`}>
                  +{combinedRouteStats.estimatedOTHours.toFixed(1)}h
                </Typography>
                <Typography className={classes.statLabel}>
                  <WarningIcon fontSize="small" style={{ color: '#ff9800' }} /> Est. OT
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {optimizeError && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {optimizeError}
          </Typography>
        )}
      </Paper>

      {/* Crew Leader Legend */}
      {allCrewLeaders.length > 0 && (
        <Paper className={classes.legend}>
          <Typography variant="caption" style={{ width: '100%', marginBottom: 4 }}>
            Crew Leaders:
          </Typography>
          {allCrewLeaders.map(crew => {
            const isSelected = selectedCrewLeaders.length === 1 && selectedCrewLeaders[0] === crew.id
            return (
              <Chip
                key={crew.id}
                label={crew.name}
                size="small"
                onClick={() => {
                  if (isSelected) {
                    setSelectedCrewLeaders([]) // Deselect if already selected
                  } else {
                    setSelectedCrewLeaders([crew.id]) // Select just this crew
                  }
                }}
                style={{
                  backgroundColor: isSelected ? crewColorMap.get(crew.id) : 'transparent',
                  color: isSelected ? '#fff' : 'inherit',
                  borderColor: crewColorMap.get(crew.id),
                  borderWidth: 2
                }}
                variant={isSelected ? 'default' : 'outlined'}
                icon={
                  <Box
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: crewColorMap.get(crew.id),
                      marginLeft: 8
                    }}
                  />
                }
              />
            )
          })}
        </Paper>
      )}

      {/* Map */}
      <Box className={classes.mapContainer}>
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GMAPKEY}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={defaultCenter}
            zoom={10}
            onLoad={onMapLoad}
          >
            {/* Route polyline */}
            {routePath.length > 1 && (
              <Polyline
                path={routePath}
                options={{
                  strokeColor: '#2196f3',
                  strokeOpacity: 0.8,
                  strokeWeight: 4
                }}
              />
            )}

            {/* Property markers */}
            {displayProperties.map((prop, index) => {
              const crewLeader = getCrewLeaderForProperty(prop) ||
                (prop.crewLeaderId ? { id: prop.crewLeaderId, name: prop.crewLeaderName } : null)
              const markerColor = crewLeader?.id
                ? crewColorMap.get(crewLeader.id) || '#FF5733'
                : '#999999'
              // Show numbered markers if we have optimized route OR pre-calculated route
              const hasRouteOrder = !!optimizedRoute || !!prop._fromDailyRoute
              const orderNum = prop._optimizedOrder || prop.order || index + 1

              return (
                <Marker
                  key={prop.propertyId || `prop-${index}`}
                  position={{ lat: prop.latitude, lng: prop.longitude }}
                  onClick={() => setActiveMarker(prop)}
                  label={hasRouteOrder ? {
                    text: String(orderNum),
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  } : undefined}
                  icon={hasRouteOrder ? {
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    scale: 14,
                    fillColor: markerColor,
                    fillOpacity: 0.9,
                    strokeColor: '#ffffff',
                    strokeWeight: 2
                  } : {
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    scale: 10,
                    fillColor: markerColor,
                    fillOpacity: 0.9,
                    strokeColor: '#ffffff',
                    strokeWeight: 2
                  }}
                />
              )
            })}

            {activeMarker && (
              <InfoWindow
                position={{ lat: activeMarker.latitude, lng: activeMarker.longitude }}
                onCloseClick={() => setActiveMarker(null)}
              >
                <div className={classes.infoWindow}>
                  {(activeMarker._optimizedOrder || activeMarker.order) && (
                    <Typography className={classes.infoOrder}>
                      Stop #{activeMarker._optimizedOrder || activeMarker.order}
                    </Typography>
                  )}
                  <Typography className={classes.infoTitle}>
                    {activeMarker.name}
                  </Typography>
                  <Typography className={classes.infoCrew}>
                    {activeMarker.crewLeaderName || getCrewLeaderForProperty(activeMarker)?.name || 'Unassigned'}
                  </Typography>
                  {activeMarker.estimatedHours > 0 && (
                    <Typography variant="caption" display="block">
                      Est. time: {activeMarker.estimatedHours.toFixed(1)}h
                    </Typography>
                  )}
                  {activeMarker.estimatedDriveDuration > 0 && (
                    <Typography variant="caption" display="block">
                      Drive: {Math.round(activeMarker.estimatedDriveDuration)} min, {activeMarker.distanceMiles} mi
                    </Typography>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </Box>

      {/* Empty state */}
      {displayProperties.length === 0 && !isLoadingRoutes && (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary">
            {dailyRoutes.length === 0
              ? `No routes scheduled for ${format(selectedDate, 'EEEE, MMMM d, yyyy')}`
              : `No properties scheduled for ${format(selectedDate, 'EEEE, MMMM d, yyyy')}${selectedCrewLeaders.length > 0 ? ' with selected crew leaders' : ''}`
            }
          </Typography>
          {dailyRoutes.length === 0 && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
              Run a sync to calculate routes for this date
            </Typography>
          )}
        </Box>
      )}
    </div>
  )
}

export default RoutingPropertiesMapView
